import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Cart from '@/components/front-office/orders/Cart';
import OrdersLargeContainer from '@/components/front-office/containers/OrdersLargeContainer';
import Button from '@/components/Button';
import WalletModal from '@/components/front-office/wallet/WalletModal';
import DeliveryInfoForm from '@/components/front-office/payment/DeliveryInfoForm';
import BillingInfoForm from '@/components/front-office/payment/BillingInfoForm';
import DeliveryDateSelector from '@/components/front-office/payment/DeliveryDateSelector';
import PromoCodeSection from '@/components/front-office/payment/PromoCodeSection';
import WalletSection from '@/components/front-office/payment/WalletSection';
import Modal from '@/components/front-office/Modal';
import { useOrdersContext } from '@/contexts/OrderContext';
import { usePaymentConfirmedContext } from '@/contexts/PaymentConfirmedContext';
import useDeliveryFees from '@/hooks/useDeliveryFees';
import { usePaymentValidation, validatePaymentForm } from '@/hooks/usePaymentValidation';
import { useDeliveryDates } from '@/hooks/useDeliveryDates';
import { useBillingInfo } from '@/hooks/useBillingInfo';
import { usePaymentSubmission } from '@/hooks/usePaymentSubmission';
import { applyReorderDiscount } from '@/services/paymentService';
import { showWarning, showError } from '@/utils/toastHelper';

const Payment = () => {
	const {
		cart,
		setCart,
		handleApplyPromo,
		promoId,
		setpromoId,
		promoApplied,
		handleGetTotalPrice,
		handleCancelPromo,
		setPromoApplied,
		isWalletModalOpen,
		setIsWalletModalOpen
	} = useOrdersContext();

	const { setPaymentConfirmedObject } = usePaymentConfirmedContext();
	const { handleCalculateDeliveryFee } = useDeliveryFees();
	const navigate = useNavigate();

	const [orderContextInput, setOrderContextInput] = useState({});
	const [restaurant, setRestaurant] = useState(null);
	const [isSameInfo, setIsSameInfo] = useState(true);
	const [walletBalance, setWalletBalance] = useState(null);
	const [walletDiscount, setWalletDiscount] = useState(0);
	const [showReorderConfirmModal, setShowReorderConfirmModal] = useState(false);
	const [pendingReorderCode, setPendingReorderCode] = useState('');
	const [pendingReorderEmail, setPendingReorderEmail] = useState('');
	const [pendingReorderPhone, setPendingReorderPhone] = useState('');

	const billingAddressRef = useRef(null);
	const billingLastNameRef = useRef(null);
	const billingFirstNameRef = useRef(null);
	const billingPhoneRef = useRef(null);

	const billingRefs = {
		billingAddressRef,
		billingLastNameRef,
		billingFirstNameRef,
		billingPhoneRef
	};

	const { canSubmit } = usePaymentValidation(cart, setCart, isSameInfo);
	const { deliveryOptions } = useDeliveryDates(restaurant, cart, setCart);
	const { isSubmitting, submitPayment } = usePaymentSubmission(
		cart,
		handleGetTotalPrice,
		handleCalculateDeliveryFee,
		promoApplied,
		walletDiscount,
		walletBalance,
		setPaymentConfirmedObject
	);

	useBillingInfo(isSameInfo, cart, setCart, billingRefs);

	useEffect(() => {
		const fetchRestaurantDetails = async () => {
			if (!cart.restaurant_id) return;

			try {
				const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/restaurants/${cart.restaurant_id}`);
				if (response.status === 200) {
					setRestaurant(response.data);
				}
			} catch (error) {
				// Error fetching restaurant details - component will handle gracefully
			}
		};

		fetchRestaurantDetails();
	}, [cart.restaurant_id]);

	useEffect(() => {
		if (!cart) return;

		if (cart.is_no_limit && cart.is_no_limit_type === 'list') {
			const total = handleGetTotalPrice().total + handleCalculateDeliveryFee();
			if (total < cart.is_no_limit_minimum_amount) {
				navigate('/commande/liste-des-produits');
				return;
			}
		}

		if (cart.is_no_limit && cart.is_no_limit_type === 'category' && cart.is_no_limit_selected_category) {
			const total = handleGetTotalPrice().total + handleCalculateDeliveryFee();
			if (total <= 0) {
				const categoryName = cart.is_no_limit_selected_category.name
					.toLowerCase()
					.split(' ')
					.join('-')
					.replace(',', '-');
				navigate(`/commande/${categoryName}`, { replace: true });
				return;
			}
		}

		if (!cart.menus || cart.menus.length === 0) return;

		const requiredQuantitiesAreReached = cart.categories
			?.filter(category => category.selected_products.length > 0)
			.map(category => {
				const totalQuantity = category.selected_products
					.map(product => product.selected_quantity)
					.reduce((accumulator, quantity) => accumulator + quantity, 0);
				return category.step_quantity <= totalQuantity;
			})
			.every(boolean => boolean);

		if (!requiredQuantitiesAreReached) {
			navigate('/commande/menu/categories', { replace: true });
		}
	}, [cart, navigate, handleGetTotalPrice, handleCalculateDeliveryFee]);


	const handleApplyReorderDiscountWrapper = async (code, userEmail = '', userPhone = '') => {
		if (walletDiscount > 0) {
			setPendingReorderCode(code);
			setPendingReorderEmail(userEmail);
			setPendingReorderPhone(userPhone);
			setShowReorderConfirmModal(true);
			return;
		}

		await applyReorderDiscountInternal(code, userEmail, userPhone);
	};

	const applyReorderDiscountInternal = async (code, userEmail = '', userPhone = '') => {
		setpromoId(code);
		setCart((prevState) => ({ ...prevState, promo_code: code.toUpperCase() }));

		try {
			const promo = await applyReorderDiscount(
				cart.restaurant_id,
				code,
				handleGetTotalPrice().total,
				userEmail || cart.billing_email || '',
				userPhone || cart.delivery_phone || ''
			);
			setPromoApplied(promo);
			setWalletDiscount(0);
			setWalletBalance(null);
			setIsWalletModalOpen(false);
		} catch (error) {
			if (error.message === 'ALREADY_REFERRED') {
				setpromoId('');
				setCart((prevState) => ({ ...prevState, promo_code: '' }));
				return;
			}
			setpromoId('');
			setCart((prevState) => ({ ...prevState, promo_code: '' }));
		}
	};

	const handleConfirmReorderReplaceWallet = async () => {
		setShowReorderConfirmModal(false);
		await applyReorderDiscountInternal(pendingReorderCode, pendingReorderEmail, pendingReorderPhone);
		setPendingReorderCode('');
		setPendingReorderEmail('');
		setPendingReorderPhone('');
	};

	const handleCancelReorderReplaceWallet = () => {
		setShowReorderConfirmModal(false);
		setPendingReorderCode('');
		setPendingReorderEmail('');
		setPendingReorderPhone('');
	};

	const handleApplyPromoWrapper = async () => {
		if (walletDiscount > 0) {
			showError('Le code promotionnel n\'est pas cumulable avec le wallet. Veuillez annuler la réduction pour utiliser un code promo.');
			return;
		}

		await handleApplyPromo();
	};

	const handlePromoChange = (value) => {
		setpromoId(value);
		setCart((prevState) => ({ ...prevState, promo_code: value }));
	};

	const handleDateSelect = (date) => {
		setCart((prevState) => ({ ...prevState, delivery_date: date }));
	};

	const handleCancelWallet = () => {
		setWalletDiscount(0);
		setWalletBalance(null);
		setIsWalletModalOpen(false);
	};

	const handleWalletClick = () => {
		if (promoApplied) {
			showError('Le code promotionnel n\'est pas cumulable avec le portefeuille parrainage.');
			return;
		}

		setIsWalletModalOpen(true);
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		const result = validatePaymentForm(cart, isSameInfo);
		if (!result.valid) {
			showError(result.errorMessage);
			return;
		}
		submitPayment(e);
	};

	return (
		<OrdersLargeContainer>
			<div className="col-span-3 bg-white/90 rounded-3xl shadow-2xl p-2 text-center lg:col-span-2">
				<form
					id="sidebar-content"
					className="overflow-y-auto max-h-[600px]"
					onSubmit={handleSubmit}
				>
					<div className='h-[calc(100dvh-180px)] p-2 grid gap-2 items-start justify-center overflow-x-hidden rounded-lg md:h-auto'>
						<div className="mb-0 grid grid-cols-1 place-items-center gap-4 md:mb-4 md:grid-cols-2">
							<DeliveryInfoForm
								cart={cart}
								setCart={setCart}
								setOrderContextInput={setOrderContextInput}
								isSameInfo={isSameInfo}
								onSameInfoToggle={setIsSameInfo}
							/>

							<BillingInfoForm
								cart={cart}
								setCart={setCart}
								isSameInfo={isSameInfo}
								refs={billingRefs}
							/>
						</div>

						<div className="grid grid-cols-2 gap-2 md:grid-cols-1">
							<DeliveryDateSelector
								deliveryOptions={deliveryOptions}
								selectedDate={cart.delivery_date}
								onDateSelect={handleDateSelect}
							/>

							<PromoCodeSection
								promoApplied={promoApplied}
								promoId={promoId}
								onPromoChange={handlePromoChange}
								onApply={handleApplyPromoWrapper}
								onCancel={handleCancelPromo}
								walletDiscount={walletDiscount}
								onCancelWallet={handleCancelWallet}
							/>
						</div>

						<WalletSection
							walletDiscount={walletDiscount}
							onWalletClick={handleWalletClick}
						/>
					</div>

					<div>
						<Button
							text={promoApplied?.discountType === 'free' ? 'Valider la commande' : 'Payer'}
							type={'Button'}
							action="submit"
							withTopMargin={false}
							extraClasses="max-w-xl mx-auto lg:block hidden"
							disabled={canSubmit && !isSubmitting}
						/>
					</div>
				</form>

				{deliveryOptions.length > 0 && (
					<div className='w-full text-center text-sm font-bold md:hidden'>
						Scrollez pour choisir une date de livraison
					</div>
				)}
			</div>

			<div className="hidden lg:block">
				<Cart showNextButton={false} isStepper={false} walletDiscount={walletDiscount} />
			</div>

			<WalletModal
				isOpen={isWalletModalOpen}
				onClose={() => setIsWalletModalOpen(false)}
				onDiscountAmountChange={(amount) => {
					setWalletDiscount(amount);
					if (amount > 0) {
						setWalletBalance(amount);
					}
				}}
				onApplyPromoCode={handleApplyReorderDiscountWrapper}
				maxDiscountAmount={
					handleGetTotalPrice().totalWithPromo >= 0 && promoApplied
						? handleGetTotalPrice().totalWithPromo
						: handleGetTotalPrice().total
				}
				userEmail={cart.billing_email}
				userPhone={cart.delivery_phone}
			/>

			<Modal
				showModal={showReorderConfirmModal}
				setShowModal={setShowReorderConfirmModal}
				headerTitle="Confirmation"
				handleSubmit={handleConfirmReorderReplaceWallet}
				handleCancelModalButton={handleCancelReorderReplaceWallet}
				closeButtonText="Annuler"
				submitButtonText="Confirmer"
				withHeader={true}
			>
				<p className="text-gray-700">
					Une réduction est actuellement appliquée. L'application de ce code de réduction remplacera la réduction.
					<br />
					<br />
					Êtes-vous sûr de vouloir continuer ?
				</p>
			</Modal>
		</OrdersLargeContainer>
	);
};

export default Payment;
